import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ModalPortal from '@/components/ModalPortal';
import EquipmentComponentsEditor, { EquipmentComponentDraft } from '@/components/EquipmentComponentsEditor';
import SpecificationEditor, { cleanSpecifications } from '@/components/SpecificationEditor';
import InfiniteScroll from '@/components/InfiniteScroll';
import useInfiniteRecords from '@/hooks/useInfiniteRecords';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircuitBoard,
    Edit2,
    Eye,
    HardDrive,
    Layers3,
    Loader2,
    MapPin,
    Monitor,
    Package,
    Plus,
    RotateCcw,
    Save,
    Search,
    Settings2,
    Trash2,
    Wrench,
    X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import api from '@/api/axios';
import {
    Equipment,
    EquipmentModel,
    Item,
    Local,
    OperatingSystem,
    Specification,
    Unit, Brand, ItemType, SpecificationKey,
} from '@/types/types';
import { useAuth } from '@/auth/AuthContext';


interface EquipmentFormState {
    id: string;
    status: string;
    patrimony: string;
    serialNumber: string;
    modelId: string;
    equipmentTypeId: string; brandId: string; osFamilyId: string;
    components: EquipmentComponentDraft[];
    unitId: string;
    localId: string;
    operatingSystemId: string;
    specifications: Record<string, string>;
}

interface SpringPage<T> {
    content?: T[];
    totalElements?: number;
    totalPages?: number;
    number?: number;
    page?: {
        size: number;
        number: number;
        totalElements: number;
        totalPages: number;
    };
}

const createEmptyForm = (): EquipmentFormState => ({
    id: '',
    status: 'ACTIVE',
    patrimony: '',
    serialNumber: '',
    modelId: '', equipmentTypeId: '', brandId: '', osFamilyId: '', components: [],
    unitId: '',
    localId: '',
    operatingSystemId: '',
    specifications: {},
});

const STATUS_CONFIG: Record<string, { label: string; color: string; background: string; border: string }> = {
    ACTIVE: {
        label: 'Ativo',
        color: 'text-emerald-400',
        background: 'bg-emerald-500/10',
        border: 'border-emerald-500/20',
    },
    STOCK: {
        label: 'Em estoque',
        color: 'text-blue-400',
        background: 'bg-blue-500/10',
        border: 'border-blue-500/20',
    },
    INSTALLED: {
        label: 'Instalado',
        color: 'text-cyan-400',
        background: 'bg-cyan-500/10',
        border: 'border-cyan-500/20',
    },
    MAINTENANCE: {
        label: 'Em manutenção',
        color: 'text-amber-400',
        background: 'bg-amber-500/10',
        border: 'border-amber-500/20',
    },
    EXTERNAL_REPAIR: {
        label: 'Manutenção externa',
        color: 'text-orange-400',
        background: 'bg-orange-500/10',
        border: 'border-orange-500/20',
    },
    DISCARDED: {
        label: 'Baixado',
        color: 'text-red-400',
        background: 'bg-red-500/10',
        border: 'border-red-500/20',
    },
};

const getStatus = (status?: string) => STATUS_CONFIG[status || ''] ?? {
    label: status || 'Não informado',
    color: 'text-slate-400',
    background: 'bg-white/5',
    border: 'border-white/10',
};

const pageContent = <T,>(data: SpringPage<T> | T[]): T[] => {
    if (Array.isArray(data)) return data;
    return Array.isArray(data.content) ? data.content : [];
};

const sortSpecifications = (specifications: Specification[] = []) => [...specifications].sort((left, right) =>
    (left.key?.displayOrder ?? 999) - (right.key?.displayOrder ?? 999)
    || (left.key?.displayName || '').localeCompare(right.key?.displayName || '')
);

const specificationValue = (specification: Specification) => {
    const value = specification.value?.trim() || 'Não informado';
    const unit = specification.key?.unit?.trim();
    return unit && value !== 'Não informado' ? `${value} ${unit}` : value;
};

const formatDate = (value?: string) => {
    if (!value) return 'Não informado';
    const normalized = value.length === 10 ? `${value}T12:00:00` : value;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR').format(date);
};

const getItemTitle = (item: Item) => {
    const type = item.itemType?.name || item.model?.itemType?.name;
    return [type, item.brand?.name, item.model?.name].filter(Boolean).join(' · ') || 'Componente sem identificação';
};

const AssetComposerPage: React.FC = () => {
    const { user, activeSecretary } = useAuth();
    const navigate=useNavigate();const route=useLocation();const {id:editId}=useParams<{id:string}>();
    const isForm=route.pathname.endsWith('/new') || !!editId;

    const [models, setModels] = useState<EquipmentModel[]>([]);
    const [specificationKeys,setSpecificationKeys] = useState<SpecificationKey[]>([]);
    const [componentTypes,setComponentTypes] = useState<ItemType[]>([]);
    const [brands,setBrands] = useState<Brand[]>([]);
    const [referencesReady,setReferencesReady] = useState(false);
    const [systems, setSystems] = useState<OperatingSystem[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [allLocations, setAllLocations] = useState<Local[]>([]);
    const [form, setForm] = useState<EquipmentFormState>(createEmptyForm);

    const [search, setSearch] = useState('');
    const [filterUnitId, setFilterUnitId] = useState('ALL');
    const [filterLocalId, setFilterLocalId] = useState('ALL');
    const [filterStatus, setFilterStatus] = useState('ALL');

    const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const canDelete = ['ADMIN', 'TECHNICAL_ADMIN', 'TECHNICAL'].includes(user?.role || '');

    const formLocations = useMemo(
        () => allLocations.filter(location => location.unit?.id === form.unitId),
        [allLocations, form.unitId],
    );

    const filterLocations = useMemo(
        () => filterUnitId === 'ALL'
            ? allLocations
            : allLocations.filter(location => location.unit?.id === filterUnitId),
        [allLocations, filterUnitId],
    );

    const hasActiveFilters = Boolean(
        search.trim() || filterUnitId !== 'ALL' || filterLocalId !== 'ALL' || filterStatus !== 'ALL',
    );

    const query = useInfiniteRecords<Equipment>('/equipment/filter', {
        search: search.trim() || undefined, unitId: filterUnitId === 'ALL' ? undefined : filterUnitId,
        localId: filterLocalId === 'ALL' ? undefined : filterLocalId, status: filterStatus === 'ALL' ? undefined : filterStatus, sort: 'patrimony,asc',
    }, activeSecretary?.id);
    const { items: equipments, loading, total: totalElements, reload: loadEquipments } = query;
    useEffect(() => {
        const controller=new AbortController();
        const all=async <T,>(path:string,params:Record<string,string>={}):Promise<T[]>=>{
            const result:T[]=[];let page=0;let total=1;
            do { const r=await api.get(path,{params:{...params,page,size:100},signal:controller.signal});result.push(...pageContent<T>(r.data));total=(r.data.page || r.data).totalPages ?? 1;page++; } while(page<total);
            return result;
        };
        Promise.all([all<EquipmentModel>('/equipment-model/all'),all<OperatingSystem>('/os/all'),all<SpecificationKey>('/specification-Key/all'),all<ItemType>('/item-type/all',{category:'COMPONENT'}),all<Brand>('/brand/all')])
            .then(([m,o,k,t,b])=>{if(!controller.signal.aborted){setModels(m);setSystems(o);setSpecificationKeys(k);setComponentTypes(t);setBrands(b);setReferencesReady(true);}})
            .catch(()=>{if(!controller.signal.aborted)setMessage({type:'error',text:'Não foi possível carregar os modelos, componentes e chaves de especificação. Atualize a página.'});});
        return ()=>controller.abort();
    }, []);
    const selectedModel=models.find(m=>m.id===form.modelId);
    const equipmentTypes=[...new Map(models.filter(m=>m.equipmentType).map(m=>[m.equipmentType.id,m.equipmentType])).values()];
    const modelOptions=models.filter(m=>(!form.equipmentTypeId || m.equipmentType?.id===form.equipmentTypeId) && (!form.brandId || m.brand?.id===form.brandId));
    const osFamilies=[...new Map(systems.filter(o=>o.family).map(o=>[o.family.id,o.family])).values()];
    const equipmentSuggestions=(()=>{const type=(selectedModel?.equipmentType?.name || '').toLocaleLowerCase('pt-BR');
        if(type==='monitor')return ['SCREEN_SIZE','RESOLUTION'];if(type==='impressora')return ['PRINT_TECHNOLOGY','COLOR_MODE'];
        if(['roteador','switch','hub','roteador sem fio'].includes(type))return ['PORT_NUMBER','WIRELESS','BAND_TYPE','ANTENNA_COUNT'];
        if(['nobreak','estabilizador'].includes(type))return ['POWER_CAPACITY','VOLTAGE'];return [];
    })();

    useEffect(() => {
        if (!activeSecretary?.id) {
            setUnits([]);
            setAllLocations([]);
            return;
        }

        const loadStructure = async () => {
            try {
                const [unitResponse, localResponse] = await Promise.all([
                    api.get(`/unit/secretary/${activeSecretary.id}`),
                    api.get<SpringPage<Local>>('/local/all', { params: { page: 0, size: 2000, sort: 'name,asc' } }),
                ]);
                setUnits(pageContent<Unit>(unitResponse.data));
                setAllLocations(pageContent(localResponse.data));
            } catch {
                setUnits([]);
                setAllLocations([]);
                setMessage({ type: 'error', text: 'Não foi possível carregar as unidades e os locais.' });
            }
        };

        setForm(createEmptyForm());
        setFilterUnitId('ALL');
        setFilterLocalId('ALL');
        loadStructure();
    }, [activeSecretary?.id]);

    useEffect(() => {
        if (filterLocalId !== 'ALL' && !filterLocations.some(location => location.id === filterLocalId)) {
            setFilterLocalId('ALL');
        }
    }, [filterLocalId, filterLocations]);

    const resetForm = () => setForm(createEmptyForm());

    const clearFilters = () => {
        setSearch('');
        setFilterUnitId('ALL');
        setFilterLocalId('ALL');
        setFilterStatus('ALL');
    };

    useEffect(()=>{if(!editId)return;let alive=true;api.get(`/equipment/${editId}`).then(r=>{if(alive)editEquipment(r.data);}).catch(()=>setMessage({type:'error',text:'Não foi possível carregar o equipamento.'}));return()=>{alive=false;};},[editId]);
    const editEquipment = (equipment: Equipment) => {
        const preservedSpecifications = Object.fromEntries(
            (equipment.specifications ?? [])
                .filter(specification => specification.key?.id)
                .map(specification => [specification.key.id, specification.value ?? '']),
        );

        setForm({
            id: equipment.id,
            status: equipment.status,
            patrimony: equipment.patrimony || '',
            serialNumber: equipment.serialNumber || '',
            modelId: equipment.model?.id || '', equipmentTypeId: equipment.model?.equipmentType?.id || '', brandId: equipment.model?.brand?.id || '', osFamilyId: equipment.operatingSystem?.family?.id || '',
            components: (equipment.items || []).filter(i=>i.status === 'INSTALLED' && i.itemType?.category !== 'SUPPLY').map(i=>({clientId:i.id,id:i.id,typeId:i.itemType.id,typeName:i.itemType.name,modelId:i.model.id,modelName:i.model.name,brandId:i.brand?.id || '',brandName:i.brand?.name || '',serialNumber:i.serialNumber || '',patrimony:i.patrimony || '',specifications:Object.fromEntries((i.specifications || []).map(s=>[s.key.id,s.value || '']))})),
            unitId: equipment.local?.unit?.id || '',
            localId: equipment.local?.id || '',
            operatingSystemId: equipment.operatingSystem?.id || '',
            specifications: preservedSpecifications,
        });
        setSelectedEquipment(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const saveEquipment = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!form.modelId || !form.localId) {
            setMessage({ type: 'error', text: 'Informe o modelo e o local do equipamento.' });
            return;
        }

        if(form.components.some(component=>!component.modelId)){setMessage({type:'error',text:'Selecione o modelo de cada componente adicionado.'});return;}
        setSaving(true);
        try {
            const payload = {
                status: form.status,
                patrimony: form.patrimony.trim() || null,
                serialNumber: form.serialNumber.trim() || null,
                modelId: form.modelId,
                localId: form.localId,
                operatingSystemId: form.operatingSystemId || null,
                specifications: cleanSpecifications(form.specifications),
                components: form.components.map(c=>({id:c.id || null,modelId:c.modelId,serialNumber:c.serialNumber.trim() || null,patrimony:c.patrimony.trim() || null,specifications:cleanSpecifications(c.specifications)})),
            };
            if (form.id) await api.put(`/equipment/${form.id}`, payload);
            else await api.post('/equipment', payload);
            setMessage({ type: 'success', text: form.id ? 'Equipamento atualizado.' : 'Equipamento cadastrado.' });
            resetForm();navigate('/asset-composer');
            await loadEquipments();
        } catch (error: any) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Não foi possível salvar o equipamento.' });
        } finally {
            setSaving(false);
        }
    };

    const discardEquipment = async (id: string) => {
        if (!window.confirm('Confirma a baixa deste equipamento?')) return;
        try {
            await api.delete(`/equipment/${id}`);
            setSelectedEquipment(null);
            setMessage({ type: 'success', text: 'Equipamento baixado com sucesso.' });
            await loadEquipments();
        } catch (error: any) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Não foi possível baixar o equipamento.' });
        }
    };

    return (
        <div className="page-content">
            <div className="space-y-6">
                <div className="ui-card flex items-center justify-between gap-4"><h1 className="text-lg font-semibold">{isForm ? 'Cadastro de equipamento' : 'Equipamentos'}</h1><button className={isForm ? 'ui-button-secondary' : 'ui-button-primary'} onClick={()=>{resetForm();navigate(isForm ? '/asset-composer' : '/asset-composer/new');}}>{isForm ? 'Voltar à listagem' : '+ Novo'}</button></div>
                <AnimatePresence mode="popLayout">
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm ${message.type === 'success'
                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                                : 'border-red-500/30 bg-red-500/10 text-red-300'}`}
                        >
                            <span>{message.text}</span>
                            <button type="button" onClick={() => setMessage(null)} aria-label="Fechar aviso">
                                <X size={16} />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {isForm && <motion.form
                    onSubmit={saveEquipment}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="ui-card ui-card-hover space-y-5"
                >
                    <div className="flex items-center justify-between gap-4">
                        <h2 className="ui-section-title flex-1">
                            <Monitor size={16} className="text-emerald-500" />
                            {form.id ? 'Editar equipamento' : 'Novo equipamento'}
                        </h2>
                        {form.id && (
                            <button type="button" onClick={()=>navigate('/asset-composer')} className="text-slate-400 transition-colors hover:text-white" aria-label="Cancelar edição">
                                <X size={18} />
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <Field label="Patrimônio">
                            <input value={form.patrimony} onChange={event => setForm(current => ({ ...current, patrimony: event.target.value }))} className="form-field" />
                        </Field>
                        <Field label="Número de série">
                            <input value={form.serialNumber} onChange={event => setForm(current => ({ ...current, serialNumber: event.target.value }))} className="form-field" />
                        </Field>
                        <Field label="Status">
                            <select value={form.status} onChange={event => setForm(current => ({ ...current, status: event.target.value }))} className="form-field">
                                {Object.entries(STATUS_CONFIG).filter(([value]) => value !== 'EXTERNAL_REPAIR' || form.status === 'EXTERNAL_REPAIR').map(([value, config]) => <option key={value} value={value}>{config.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Tipo de equipamento"><select className="form-field" value={form.equipmentTypeId} onChange={e=>setForm(current=>({...current,equipmentTypeId:e.target.value,brandId:'',modelId:''}))}><option value="">Todos os tipos</option>{equipmentTypes.map(type=><option key={type.id} value={type.id}>{type.name}</option>)}</select></Field>
                        <Field label="Marca do equipamento"><select className="form-field" value={form.brandId} onChange={e=>setForm(current=>({...current,brandId:e.target.value,modelId:''}))}><option value="">Todas as marcas</option>{brands.map(brand=><option key={brand.id} value={brand.id}>{brand.name}</option>)}</select></Field>
                        <Field label="Modelo *">
                            <select required value={form.modelId} onChange={event => setForm(current => ({ ...current, modelId: event.target.value }))} className="form-field">
                                <option value="">Selecione...</option>
                                {modelOptions.map(model => <option key={model.id} value={model.id}>{model.equipmentType?.name} · {model.brand?.name} {model.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Unidade *">
                            <select
                                required
                                value={form.unitId}
                                onChange={event => setForm(current => ({ ...current, unitId: event.target.value, localId: '' }))}
                                className="form-field"
                            >
                                <option value="">Selecione...</option>
                                {units.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Local *">
                            <select
                                required
                                disabled={!form.unitId}
                                value={form.localId}
                                onChange={event => setForm(current => ({ ...current, localId: event.target.value }))}
                                className="form-field"
                            >
                                <option value="">Selecione...</option>
                                {formLocations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Família do sistema operacional"><select className="form-field" value={form.osFamilyId} onChange={e=>setForm(current=>({...current,osFamilyId:e.target.value,operatingSystemId:''}))}><option value="">Todas as famílias</option>{osFamilies.map(family=><option key={family.id} value={family.id}>{family.name}</option>)}</select></Field>
                        <Field label="Sistema operacional">
                            <select value={form.operatingSystemId} onChange={event => setForm(current => ({ ...current, operatingSystemId: event.target.value }))} className="form-field">
                                <option value="">Não informado</option>
                                {systems.filter(system=>!form.osFamilyId || system.family?.id===form.osFamilyId).map(system => <option key={system.id} value={system.id}>{system.family?.name ? `${system.family.name} · ` : ''}{system.name}</option>)}
                            </select>
                        </Field>
                    </div>

                    {!referencesReady && <p className="text-sm text-slate-400">Carregando o catálogo de especificações e componentes...</p>}
                    <SpecificationEditor title="Especificações do equipamento" keys={specificationKeys} value={form.specifications} suggested={equipmentSuggestions} onChange={specifications=>setForm(current=>({...current,specifications}))} />
                    <EquipmentComponentsEditor value={form.components} onChange={components=>setForm(current=>({...current,components}))} types={componentTypes} brands={brands} keys={specificationKeys} />
                    <div className="flex flex-wrap justify-end gap-3">
                        <button type="button" onClick={()=>editId ? navigate('/asset-composer') : resetForm()} className="ui-button-secondary">{editId ? 'Cancelar' : 'Limpar'}</button>
                        <button disabled={saving || !referencesReady || (!!editId && form.id!==editId)} className="ui-button-primary">
                            {saving ? <Loader2 size={16} className="animate-spin" /> : form.id ? <Save size={16} /> : <Plus size={16} />}
                            {form.id ? 'Atualizar' : 'Cadastrar'}
                        </button>
                    </div>
                </motion.form>}

                {!isForm && <motion.section
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 }}
                    className="ui-card space-y-5"
                >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-white">
                                <Layers3 size={17} className="text-blue-400" /> Inventário de máquinas
                            </h2>
                            <p className="mt-1 text-xs text-slate-500">
                                {loading ? 'Consultando inventário...' : `${totalElements} ${totalElements === 1 ? 'máquina encontrada' : 'máquinas encontradas'}`}
                            </p>
                        </div>
                        {hasActiveFilters && (
                            <button type="button" onClick={clearFilters} className="ui-button-secondary self-start sm:self-auto">
                                <RotateCcw size={14} /> Limpar filtros
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <Field label="Pesquisar">
                            <div className="relative">
                                <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    value={search}
                                    onChange={event => setSearch(event.target.value)}
                                    placeholder="Patrimônio, série, modelo..."
                                    className="form-field pl-11"
                                />
                            </div>
                        </Field>
                        <Field label="Unidade">
                            <select
                                value={filterUnitId}
                                onChange={event => {
                                    setFilterUnitId(event.target.value);
                                    setFilterLocalId('ALL');
                                }}
                                className="form-field"
                            >
                                <option value="ALL">Todas as unidades</option>
                                {units.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Local">
                            <select value={filterLocalId} onChange={event => setFilterLocalId(event.target.value)} className="form-field">
                                <option value="ALL">Todos os locais</option>
                                {filterLocations.map(location => (
                                    <option key={location.id} value={location.id}>
                                        {filterUnitId === 'ALL' && location.unit?.name ? `${location.unit.name} · ` : ''}{location.name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Situação">
                            <select value={filterStatus} onChange={event => setFilterStatus(event.target.value)} className="form-field">
                                <option value="ALL">Todas as situações</option>
                                {Object.entries(STATUS_CONFIG).map(([value, config]) => <option key={value} value={value}>{config.label}</option>)}
                            </select>
                        </Field>
                    </div>

                    {loading && !equipments.length ? (
                        <div className="flex justify-center p-14"><Loader2 className="animate-spin text-blue-500" /></div>
                    ) : equipments.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-12 text-center">
                            <Monitor className="mx-auto mb-3 text-slate-600" size={32} />
                            <p className="text-sm font-bold text-slate-400">Nenhuma máquina encontrada.</p>
                            <p className="mt-1 text-xs text-slate-600">Tente remover algum filtro ou pesquisar por outro termo.</p>
                        </div>
                    ) : (
                        <motion.div layout className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                            <AnimatePresence mode="popLayout">
                                {equipments.map((equipment, index) => (
                                    <EquipmentCard
                                        key={equipment.id}
                                        equipment={equipment}
                                        index={index}
                                        canDelete={canDelete}
                                        onView={() => setSelectedEquipment(equipment)}
                                        onEdit={() => navigate(`/asset-composer/${equipment.id}/edit`)}
                                        onDiscard={() => discardEquipment(equipment.id)}
                                    />
                                ))}
                            </AnimatePresence>
                        </motion.div>
                    )}

                    {query.error && <p role="alert" className="feedback-error">{query.error}</p>}<InfiniteScroll hasMore={query.hasMore} loading={loading} onLoadMore={query.loadMore} />
                </motion.section>}
            </div>

            <AnimatePresence>
                {selectedEquipment && (
                    <EquipmentDetailsModal
                        equipment={selectedEquipment}
                        canDelete={canDelete}
                        onClose={() => setSelectedEquipment(null)}
                        onEdit={() => {setSelectedEquipment(null);navigate(`/asset-composer/${selectedEquipment.id}/edit`);}}
                        onDiscard={() => discardEquipment(selectedEquipment.id)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

const EquipmentCard = ({
    equipment,
    index,
    canDelete,
    onView,
    onEdit,
    onDiscard,
}: {
    equipment: Equipment;
    index: number;
    canDelete: boolean;
    onView: () => void;
    onEdit: () => void;
    onDiscard: () => void;
}) => {
    const status = getStatus(equipment.status);
    const specifications = sortSpecifications(equipment.specifications ?? []);
    const summary = specifications.slice(0, 3);

    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ delay: Math.min(index * 0.025, 0.25) }}
            className="group rounded-2xl border border-white/5 bg-white/5 p-4 transition-all hover:-translate-y-0.5 hover:border-blue-500/20 hover:bg-white/10"
        >
            <div className="flex items-start justify-between gap-3">
                <button type="button" onClick={onView} className="min-w-0 flex-1 text-left">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-lg bg-blue-500/10 px-2 py-1 text-[9px] font-black uppercase tracking-wider text-blue-400">
                            {equipment.model?.equipmentType?.name || 'Equipamento'}
                        </span>
                        <span className={`rounded-lg border px-2 py-1 text-[9px] font-black uppercase tracking-wider ${status.background} ${status.border} ${status.color}`}>
                            {status.label}
                        </span>
                    </div>
                    <h3 className="truncate text-sm font-black text-white">
                        {[equipment.model?.brand?.name, equipment.model?.name].filter(Boolean).join(' ') || 'Modelo não informado'}
                    </h3>
                    <p className="mt-1 flex items-center gap-1 truncate text-xs text-slate-500">
                        <MapPin size={11} />
                        {[equipment.local?.unit?.name, equipment.local?.name].filter(Boolean).join(' · ') || 'Local não informado'}
                    </p>
                </button>
                <div className="flex shrink-0 gap-1">
                    <ActionButton title="Ver ficha completa" onClick={onView} color="blue"><Eye size={15} /></ActionButton>
                    <ActionButton title="Editar equipamento" onClick={onEdit} color="emerald"><Edit2 size={15} /></ActionButton>
                    {canDelete && <ActionButton title="Dar baixa" onClick={onDiscard} color="red"><Trash2 size={15} /></ActionButton>}
                </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
                <CompactInfo label="Patrimônio" value={equipment.patrimony || 'Não informado'} />
                <CompactInfo label="Número de série" value={equipment.serialNumber || 'Não informado'} />
                <CompactInfo label="Sistema" value={equipment.operatingSystem?.name || 'Não informado'} wide />
            </div>

            {summary.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-white/5 pt-3">
                    {summary.map(specification => (
                        <span key={specification.id || specification.key.id} className="rounded-lg border border-white/5 bg-white/5 px-2 py-1 text-[10px] text-slate-400">
                            <strong className="text-slate-300">{specification.key?.displayName}:</strong> {specificationValue(specification)}
                        </span>
                    ))}
                    {specifications.length > summary.length && (
                        <span className="px-1 py-1 text-[10px] font-bold text-blue-400">+{specifications.length - summary.length}</span>
                    )}
                </div>
            )}

            <button type="button" onClick={onView} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-white/5 bg-white/5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 transition-colors hover:border-blue-500/20 hover:text-blue-400">
                <Eye size={13} /> Ver ficha completa
            </button>
        </motion.article>
    );
};

const EquipmentDetailsModal = ({
    equipment,
    canDelete,
    onClose,
    onEdit,
    onDiscard,
}: {
    equipment: Equipment;
    canDelete: boolean;
    onClose: () => void;
    onEdit: () => void;
    onDiscard: () => void;
}) => {
    const status = getStatus(equipment.status);
    const specifications = sortSpecifications(equipment.specifications ?? []);
    const items = equipment.items ?? [];
    const repairs = equipment.outsourcedRepairs ?? [];

    return (
        <ModalPortal><motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm md:p-6"
            onMouseDown={event => event.target === event.currentTarget && onClose()}
        >
            <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.98 }}
                transition={{ type: 'spring', damping: 28, stiffness: 330 }}
                role="dialog"
                aria-modal="true"
                aria-label="Ficha completa do equipamento"
                className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-slate-950 shadow-2xl"
            >
                <div className="flex items-start justify-between gap-4 border-b border-white/5 bg-white/5 p-5 md:p-6">
                    <div className="min-w-0">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="rounded-lg bg-blue-500/10 px-2 py-1 text-[9px] font-black uppercase tracking-widest text-blue-400">
                                {equipment.model?.equipmentType?.name || 'Equipamento'}
                            </span>
                            <span className={`rounded-lg border px-2 py-1 text-[9px] font-black uppercase tracking-widest ${status.background} ${status.border} ${status.color}`}>
                                {status.label}
                            </span>
                        </div>
                        <h2 className="truncate text-xl font-black text-white md:text-2xl">
                            {[equipment.model?.brand?.name, equipment.model?.name].filter(Boolean).join(' ') || 'Máquina sem modelo'}
                        </h2>
                        <p className="mt-1 text-xs text-slate-500">Ficha completa do ativo · ID {equipment.id}</p>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-xl border border-white/5 bg-white/5 p-2 text-slate-400 transition-colors hover:text-white" aria-label="Fechar ficha">
                        <X size={20} />
                    </button>
                </div>

                <div className="max-h-[calc(92vh-166px)] space-y-5 overflow-y-auto p-5 md:p-6">
                    <DetailSection title="Identificação e localização" icon={<Monitor size={16} />}>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <DetailValue label="Tipo" value={equipment.model?.equipmentType?.name} />
                            <DetailValue label="Marca" value={equipment.model?.brand?.name} />
                            <DetailValue label="Modelo" value={equipment.model?.name} />
                            <DetailValue label="Situação" value={status.label} />
                            <DetailValue label="Patrimônio" value={equipment.patrimony} mono />
                            <DetailValue label="Número de série" value={equipment.serialNumber} mono />
                            <DetailValue label="Secretaria" value={equipment.local?.unit?.secretary?.name} />
                            <DetailValue label="Unidade" value={equipment.local?.unit?.name} />
                            <DetailValue label="Local" value={equipment.local?.name} />
                        </div>
                    </DetailSection>

                    <DetailSection title="Sistema operacional" icon={<HardDrive size={16} />}>
                        {equipment.operatingSystem ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <DetailValue label="Família" value={equipment.operatingSystem.family?.name} />
                                <DetailValue label="Versão / distribuição" value={equipment.operatingSystem.name} />
                            </div>
                        ) : <EmptyDetail text="Sistema operacional não informado no cadastro." />}
                    </DetailSection>

                    <DetailSection title={`Especificações da máquina (${specifications.length})`} icon={<Settings2 size={16} />}>
                        {specifications.length > 0 ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                {specifications.map(specification => (
                                    <DetailValue
                                        key={specification.id || specification.key.id}
                                        label={specification.key?.displayName || specification.key?.keyName || 'Especificação'}
                                        value={specificationValue(specification)}
                                        help={specification.key?.specificationKeyCategory}
                                    />
                                ))}
                            </div>
                        ) : <EmptyDetail text="Nenhuma especificação foi cadastrada para esta máquina." />}
                    </DetailSection>

                    <DetailSection title={`Componentes instalados (${items.length})`} icon={<CircuitBoard size={16} />}>
                        {items.length > 0 ? (
                            <div className="space-y-3">
                                {items.map(item => <ItemDetails key={item.id} item={item} />)}
                            </div>
                        ) : <EmptyDetail text="Nenhuma peça ou componente está vinculado a esta máquina." />}
                    </DetailSection>

                    <DetailSection title={`Manutenções terceirizadas (${repairs.length})`} icon={<Wrench size={16} />}>
                        {repairs.length > 0 ? (
                            <div className="space-y-3">
                                {repairs.map(repair => (
                                    <div key={repair.id} className="rounded-2xl border border-white/5 bg-white/5 p-4">
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                            <h4 className="text-sm font-bold text-white">{repair.company?.fantasyName || 'Empresa não informada'}</h4>
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                                Envio {formatDate(repair.shippingDate)} · Retorno {formatDate(repair.returnDate)}
                                            </p>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                            <DetailValue label="Problema relatado" value={repair.problemReported} />
                                            <DetailValue label="Serviço realizado" value={repair.servicePerformed} />
                                            <DetailValue label="CNPJ" value={repair.company?.cnpj} mono />
                                            <DetailValue label="Contato" value={[repair.company?.contact, repair.company?.telephone].filter(Boolean).join(' · ')} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : <EmptyDetail text="Não existem manutenções terceirizadas vinculadas a esta máquina." />}
                    </DetailSection>
                </div>

                <div className="flex flex-wrap justify-end gap-2 border-t border-white/5 bg-white/5 p-4 md:px-6">
                    {canDelete && (
                        <button type="button" onClick={onDiscard} className="ui-button-secondary text-red-400">
                            <Trash2 size={15} /> Dar baixa
                        </button>
                    )}
                    <button type="button" onClick={onEdit} className="ui-button-primary">
                        <Edit2 size={15} /> Editar equipamento
                    </button>
                </div>
            </motion.div>
        </motion.div></ModalPortal>
    );
};

const ItemDetails = ({ item }: { item: Item }) => {
    const status = getStatus(item.status);
    const specifications = sortSpecifications(item.specifications ?? []);

    return (
        <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h4 className="flex items-center gap-2 text-sm font-bold text-white"><Package size={14} className="text-blue-400" />{getItemTitle(item)}</h4>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">ID {item.id}</p>
                </div>
                <span className={`self-start rounded-lg border px-2 py-1 text-[9px] font-black uppercase tracking-wider ${status.background} ${status.border} ${status.color}`}>
                    {status.label}
                </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <DetailValue label="Quantidade" value={String(item.quantity ?? 0)} />
                <DetailValue label="Patrimônio" value={item.patrimony} mono />
                <DetailValue label="Número de série" value={item.serialNumber} mono />
                <DetailValue label="Ranking do modelo" value={item.model?.ranking == null ? undefined : String(item.model.ranking)} />
            </div>
            {specifications.length > 0 && (
                <div className="mt-3 grid grid-cols-1 gap-2 border-t border-white/5 pt-3 sm:grid-cols-2 lg:grid-cols-3">
                    {specifications.map(specification => (
                        <DetailValue key={specification.id || specification.key.id} label={specification.key?.displayName || specification.key?.keyName} value={specificationValue(specification)} />
                    ))}
                </div>
            )}
        </div>
    );
};

const DetailSection = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
    <section className="rounded-2xl border border-white/5 bg-white/[0.025] p-4 md:p-5">
        <h3 className="mb-4 flex items-center gap-2 border-b border-white/5 pb-3 text-[11px] font-black uppercase tracking-[0.15em] text-white">
            <span className="text-blue-400">{icon}</span>{title}
        </h3>
        {children}
    </section>
);

const DetailValue = ({ label, value, mono = false, help }: { label: string; value?: string | null; mono?: boolean; help?: string }) => (
    <div className="min-w-0 rounded-xl border border-white/5 bg-white/5 px-3 py-2.5">
        <p className="text-[9px] font-black uppercase tracking-wider text-slate-600">{label}</p>
        <p className={`mt-1 break-words text-xs font-semibold text-slate-300 ${mono ? 'font-mono' : ''}`}>{value?.trim() || 'Não informado'}</p>
        {help && <p className="mt-1 text-[9px] uppercase tracking-wider text-slate-600">{help.replaceAll('_', ' ')}</p>}
    </div>
);

const EmptyDetail = ({ text }: { text: string }) => (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-white/10 bg-white/5 px-4 py-5 text-xs text-slate-500">
        <Box size={17} className="shrink-0" />{text}
    </div>
);

const CompactInfo = ({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) => (
    <div className={`min-w-0 rounded-xl border border-white/5 bg-white/5 px-3 py-2 ${wide ? 'col-span-2' : ''}`}>
        <p className="text-[8px] font-black uppercase tracking-wider text-slate-600">{label}</p>
        <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-300" title={value}>{value}</p>
    </div>
);

const ActionButton = ({ title, onClick, color, children }: { title: string; onClick: () => void; color: 'blue' | 'emerald' | 'red'; children: React.ReactNode }) => {
    const colorClass = {
        blue: 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20',
        emerald: 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20',
        red: 'bg-red-500/10 text-red-400 hover:bg-red-500/20',
    }[color];
    return <button type="button" title={title} aria-label={title} onClick={onClick} className={`rounded-lg p-2 transition-colors ${colorClass}`}>{children}</button>;
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <label className="space-y-1 text-[10px] font-black uppercase text-slate-500">
        <span>{label}</span>
        {children}
    </label>
);

export default AssetComposerPage;
